import React, { createContext, createRef, useContext, useEffect, useRef, useState } from "react";
import TableHead from "./TableHead";
import Filters from "./Filters";
import TableRow from "./TableRow";
import ToggleColumns from "./ToggleColumns";
import ResetFilters from "./ResetFilters";
import Pagination from "./Pagination";
import Loading from "../Loading";
import TableButton from "./TableButton";
import PageHelper from "./PageHelper";
import DragPreview from "./DragPreview";
import ResponsiveTableView from "../../components/table/ResponsiveTableView.jsx";
import VoterDetailsModal from "../../components/VoterDetailsModal/VoterDetailsModal.jsx";
import { Dropdown, DropdownMenu, DropdownOption, DropdownTrigger } from "../ui/Dropdown.jsx";
import { useDispatch, useSelector } from "react-redux";
import {
  resetFilters,
  updateFilters,
} from "../../features/voterIdLookupSlice/voterIdLookupSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchFilteredList } from "../../features/voterIdLookupSlice/voterIdLookupApi";
import InformationCircle from "../svg/InformationCircle";
import { createPortal } from "react-dom";
import Input from "../ui/Input.jsx";
import Button from "../ui/Button.jsx";

const TableProvider = createContext();

export const useTable = () => {
  if (!TableProvider) console.error("TableProvider not provided");

  return useContext(TableProvider);
};

const EditModal = ({ defaultValue, close, onEdit, offsetContainer }) => {
  const [editValue, setEditValue] = useState(defaultValue);
  const modal = useRef();

  const { status } = useTable();
  const handleSubmit = async (e) => {
    e.preventDefault();
    await onEdit(editValue);
    // After submitting, check if status.edit is "idle" and then call close()
    if (status.edit === "idle") {
      close();
    }
  };

  // Sync editValue with defaultValue whenever defaultValue changes
  useEffect(() => {
    setEditValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    // Calculate so that modal is open above the ref (offsetContainer)
    const { top, left, height } = offsetContainer.getBoundingClientRect();
    const modalHeight = modal.current.getBoundingClientRect().height;
    const modalWidth = modal.current.getBoundingClientRect().width;

    // Position the modal above the ref, with a small gap (e.g., 4px)
    modal.current.style.top = `${window.scrollY + top - modalHeight - 4}px`;
    modal.current.style.left = `${left}px`;

    // If not enough space above, fallback to below (optional, for safety)
    if (top - modalHeight - 4 < 0) {
      modal.current.style.top = `${window.scrollY + top + height + 4}px`;
    }

    // Adjust horizontal position if needed
    if (left < modalWidth) {
      modal.current.style.translate = "30px 0px";
    } else {
      modal.current.style.translate = "calc(-100% - 4px) 0px";
    }
  }, []);

  // To ensure this modal is always on top of any other modal, use a very high z-index (e.g., z-[9999])
  // and also render it directly to document.body (not inside #root, which may be under other modals).
  return createPortal(
    <>
      <div onClick={close} className="fixed inset-0 z-[9999]"></div>

      <div
        ref={modal}
        className="absolute bg-white border px-3 pb-3 flex flex-col items-end z-[99999]"
        style={{
          zIndex: 99999,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
        }}
      >
        <div className="h-[52px] flex items-center w-full justify-between">
          <h6 className="text-xl font-semibold">Edit Cell</h6>

          <button
            onClick={close}
            className="border size-8 flex justify-center items-center rounded-md"
          >
            <img src="/cross.svg" alt="close" className="size-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            value={editValue}
            onChange={
              (e) => {
                  setEditValue(e.target.value)
              }
            }
            placeholder="Enter value here"
            className="!h-11"
          />

          <div className="flex gap-2 self-end">
            <Button
              type="button"
              onClick={close}
              className="bg-transparent !text-cherry"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              state={status.edit}
              disabled={
                (editValue === "") ||
                editValue === defaultValue ||
                status.edit === "loading" 
              }
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body // Use document.body to ensure it's above all other modals
  );
};

const Table = ({
  state,
  onSortChange,
  onFiltersChange,
  onToggleColumn,
  onResetFilters,
  onChangePage,
  onChangePerPage,
  onSubmit,
  onToggleSelectRow,
  onSelectAllRows,
  onStartTutorial,
  onReorder,
  onEdit,
  isFocused,
  emptyPlaceholder,
  tableActions = [],
  rowActions,
  isEdit,
  removeSelctedVoter,
  moveVoter,
  isListTableName,
  totalItems = '',
  tableName  = '',
  finalListNames,
  addVoters,
  handleDelete,
  handleDeleteAccount,
  handleUploadUser,
  handleUpdateStatus,
  handleUpdateMulticolumn,
  handleAddVotersToList
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ThirdComponent = tableActions[3]?.component;
  const FourthComponent = tableActions[4]?.component;
  const [resetKey, setResetKey] = useState(0);
  const [_, setSearchParams] = useSearchParams();
  const [sbisdFilter,setSbisdFilter] = useState(false);
  state = {
    ...state,
    onSortChange,
    onFiltersChange,
    onToggleColumn,
    onResetFilters,
    onChangePage,
    onChangePerPage,
    onSubmit,
    onToggleSelectRow,
    onSelectAllRows,
    onStartTutorial,
    onReorder,
    onEdit,
    isFocused,
    removeSelctedVoter,
    moveVoter
  };
  const [isMobile, setIsMobile] = useState(false);
  const [isVoterModalOpen, setIsVoterModalOpen] = useState(false);
  const [isVoterModalOpenDetail, setIsVoterModalOpenDetail] = useState(false);
  const [voterData, setVoterData] = useState({});
  const [voterFields, setVoterFields] = useState([]);
  const [rowDetail, setRowDetail] = useState({});
  const editBtn = useRef();
  const [isEditing, setIsEditing] = useState(false);
  const listNames = useSelector((state) => state.myList.listNames);

  const [voterDataDetail, setVoterDataDetail] = useState(
    isListTableName === "mylistTable" ? {
    move_to_another_list:'MOVE to Another List',
    remove_voter:'DELETE VOTER'
  } : isListTableName === "lookupTable" ? {add_voter:'Add Voter'} : {edit_status:'Edit Status',edit_multi_column:"Edit Multi-Column",upload_data:"Upload Data",delete_data:"Delete Data",delete_account:"Delete Account"}
  );
  const [voterFieldsDetail, setVoterFieldsDetail] = useState(isListTableName === "mylistTable" ? [
    { key: 'move_to_another_list', label: '' },
    { key: 'remove_voter', label: '' },
  ] : isListTableName === "lookupTable" ? [{key: 'add_voter', label: ''}] : [
    { key: 'edit_status', label: '' },
    { key: 'edit_multi_column', label: '' },
    { key: 'upload_data', label: '' },
    { key: 'delete_data', label: '' },
    { key: 'delete_account', label: '' }
  ]);
  const voterSpecialFields = {
    phone_number: (value) => {
      const str = value.phone ? value?.phone?.toString()?.replace(/\D/g, '') : value.phone || null;
      return (
        <div className="flex items-center flex-wrap">
            {
              str ? 
              <a
                href={`tel:+1${str}`}
                className="text-black mr-[20px]"
                onClick={e => {
                  // If the click is on the edit button, prevent the link
                  if (e.target.dataset.editbtn === "true") {
                    e.preventDefault();
                  }
                }}
              >
                  <span className='flex text-[16px] font-normal leading-[26px] tracking-normal font-mont text-lightBlack'>
                      <span className='underline whitespace-nowrap'>
                          {str ? str?.slice(0, 3) + "-" + str?.slice(3, 6) + "-" + str?.slice(6) : 'missing'}
                      </span>
                      <img
                        src="/edit.svg"
                        alt=""
                        className="size-[18px] mt-1 ml-1 cursor-pointer"
                        data-editbtn="true"
                        ref={editBtn}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsEditing((prev) => !prev);
                        }}
                      />
                      <img src='/phone.svg' alt='phone' className='ml-[20px] h-[22px] w-[22px]' />
                  </span>
              </a> : <span className="text-black">
                  <span className='flex text-[16px] font-normal leading-[26px] tracking-normal font-mont text-lightBlack'>
                      <span className='flex'>
                          {str ? str?.slice(0, 3) + "-" + str?.slice(3, 6) + "-" + str?.slice(6) : 'missing'}
                          <img src="/edit.svg" alt="" className="size-[18px] mt-1 ml-1 cursor-pointer" ref={editBtn} onClick={() => setIsEditing((prev) => !prev)}/>
                      </span>
                  </span>
              </span> 
          }
          {str ? <a href={`sms:+1${str}`} className="text-black">
              <img src='/icons8-chat-bubble-30.svg' height={22} width={22} alt='email' />
          </a> : null}
          {isEditing && (
            <EditModal
              key={rowDetail.id + (rowDetail.phone_number || '')} // force remount on value change
              defaultValue={value.phone}
              close={() => setIsEditing(false)}
              onEdit={(value) => onEdit(rowDetail, {key:'phone_number'}, value)}
              offsetContainer={editBtn.current}
          />
          )}
        </div>
      );
    },
  };
  const GeneralVotingHistory = ({ value }) => {
    const [tooltipIndex, setTooltipIndex] = useState(null);
    const containerRef = useRef(null);
    const boxRefs = useRef([]);
    
    const votes = value ? value?.split(',')?.map(v => v?.trim()) : ['-','-','-','-'];
    
    // Setup refs for each box
    if (boxRefs.current.length !== (votes?.length || 0)) {
      boxRefs.current = Array(votes?.length || 0).fill().map((_, i) => boxRefs.current[i] || createRef());
    }
    
    useEffect(() => {
      // Handle outside clicks
      function handleClickOutside(event) {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setTooltipIndex(null);
        }
      }
      // Add event listener
      document.addEventListener("mousedown", handleClickOutside);
      
      // Clean up
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);
  
    function getTooltipName(index) {
      switch (index) {
        case 0:
          return "Nov 2024";
        case 1:
          return "Nov 2022";
        case 2:
          return "Nov 2020";
        case 3:
          return "Nov 2018";
        default:
          return "";
      }
    }
    
    return (
      <div className="relative" ref={containerRef}>
        <div className="flex space-x-2 items-center">
          {votes?.slice(0,4)?.map((vote, idx) => (
            <div
              key={idx}
              ref={el => boxRefs.current[idx] = el}
              className="w-[30px] h-[30px] cursor-pointer border border-[#E5E7EB] flex items-center justify-center rounded-md text-[18px] font-normal leading-[29px] tracking-normal font-mont text-lightBlack"
              onClick={() => setTooltipIndex(idx)}
            >
              {vote}
            </div>
          ))}
        </div>
        {tooltipIndex !== null && boxRefs.current[tooltipIndex] && (
          <div 
            className="absolute z-[1] bg-white p-2 rounded-md shadow-md border border-gray-200 w-max"
            style={{
              left: tooltipIndex === 3 ? boxRefs.current[tooltipIndex].offsetLeft - 50 : boxRefs.current[tooltipIndex].offsetLeft,
              top: boxRefs.current[tooltipIndex].offsetHeight + boxRefs.current[tooltipIndex].offsetTop + 8,
            }}
          >
            {getTooltipName(tooltipIndex)}
          </div>
        )}
      </div>
    );
  };
  
  const generalVotingHistory = {
    general_voting_history: (value) => <GeneralVotingHistory value={value} />
  };
  const PrimaryVotingHistory = ({ value }) => {
    const [tooltipIndex, setTooltipIndex] = useState(null);
    const containerRef = useRef(null);
    const boxRefs = useRef([]);
    
    const votes = value ? value?.split(',')?.map(v => v?.trim()) : ['-','-','-','-'] ;
    
    // Setup refs for each box
    if (boxRefs.current.length !== (votes?.length || 0)) {
      boxRefs.current = Array(votes?.length || 0).fill().map((_, i) => boxRefs.current[i] || createRef());
    }
    
    useEffect(() => {
      // Handle outside clicks
      function handleClickOutside(event) {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setTooltipIndex(null);
        }
      }
      // Add event listener
      document.addEventListener("mousedown", handleClickOutside);
     
      // Clean up
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);
  
    function getTooltipName(index) {
      switch (index) {
        case 0:
          return "Mar 2023";
        case 1:
          return "Mar 2021";
        case 2:
          return "Mar 2019";
        case 3:
          return "Mar 2017";
        default:
          return "";
      }
    }
    
    return (
      <div className="relative" ref={containerRef}>
        <div className="flex space-x-2 items-center">
          {votes?.slice(0,4)?.map((vote, idx) => (
            <div
              key={idx}
              ref={el => boxRefs.current[idx] = el}
              className="w-[30px] h-[30px] cursor-pointer border border-[#E5E7EB] flex items-center justify-center rounded-md text-[18px] font-normal leading-[29px] tracking-normal font-mont text-lightBlack"
              onClick={() => setTooltipIndex(idx)}
            >
              {vote}
            </div>
          ))}
        </div>
        {tooltipIndex !== null && boxRefs.current[tooltipIndex] && (
          <div 
            className="absolute z-[1] bg-white p-2 rounded-md shadow-md border border-gray-200 w-max"
            style={{
              left: tooltipIndex === 3 ? boxRefs.current[tooltipIndex].offsetLeft - 50 : boxRefs.current[tooltipIndex].offsetLeft,
              top: boxRefs.current[tooltipIndex].offsetHeight + boxRefs.current[tooltipIndex].offsetTop + 8,
            }}
          >
            {getTooltipName(tooltipIndex)}
          </div>
        )}
      </div>
    );
  };
  const primaryVotingHistory = {
    primary_voting_history: (value) => <PrimaryVotingHistory value={value} />
  };
  const removeVoter  = {
    remove_voter: (value) => {
      const handleClickEvent = ( ) => {
        removeSelctedVoter(rowDetail.id);
        setIsVoterModalOpenDetail(false);
        setIsVoterModalOpen(false);
      }
      return (
        <div className="cursor-pointer text-[16px] leading-[26px] tracking-normal font-mont text-cherry opacity-100 font-bold" onClick={handleClickEvent}>
          {value}
        </div>
      );
    }
  };
  const deleteData  = {
    delete_data: (value) => {
      const handleClickEvent = ( ) => {
        handleDelete(rowDetail.email);
        setIsVoterModalOpenDetail(false);
        setIsVoterModalOpen(false);
      }
      return (
        <div className="cursor-pointer text-[16px] font-medium leading-[26px] tracking-normal font-mont text-cherry opacity-100" onClick={handleClickEvent}>
          {value}
        </div>
      );
    }
  };
  const deleteAccount  = {
    delete_account: (value) => {
      const handleClickEvent = ( ) => {
        handleDeleteAccount(rowDetail.email);
        setIsVoterModalOpenDetail(false);
        setIsVoterModalOpen(false);
      }
      return (
        <div className="cursor-pointer text-[16px] font-medium leading-[26px] tracking-normal font-mont text-cherry opacity-100" onClick={handleClickEvent}>
          {value}
        </div>
      );
    }
  };
  const uploadData  = {
    upload_data: (value) => {
      const handleClickEvent = ( ) => {
        handleUploadUser(rowDetail);
        setIsVoterModalOpenDetail(false);
        setIsVoterModalOpen(false);
      }
      return (
        <div className="cursor-pointer text-[16px] font-medium leading-[26px] tracking-normal font-mont" onClick={handleClickEvent}>
          {value}
        </div>
      );
    }
  };
  const editStatus  = {
    edit_status: (value) => {
      const handleClick = (val) => {
        handleUpdateStatus(rowDetail.email,val);
        setIsVoterModalOpenDetail(false);
        setIsVoterModalOpen(false);
      }
      return (
        <div className="flex">
          <Dropdown>
            <DropdownTrigger className="text-[16px] font-medium leading-[26px] tracking-normal font-mont">{value}</DropdownTrigger>
              <DropdownMenu className="w-full mt-1 bg-white border rounded-md shadow-lg">
                <DropdownOption onClick={() => handleClick("active")} className="text-green-600 capitalize px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                  Approve
                </DropdownOption>
                <DropdownOption onClick={() => handleClick("denied")} className="capitalize px-3 py-2 text-sm text-cherry opacity-100 hover:bg-gray-100 cursor-pointer">
                  Deny
                </DropdownOption>
              </DropdownMenu>
          </Dropdown>
        </div>
      );
    }
  };
  const editMultiColumn  = {
    edit_multi_column: (value) => {
      const handleClick = (val) => {
        handleUpdateMulticolumn(rowDetail.email,val);
        setIsVoterModalOpenDetail(false);
        setIsVoterModalOpen(false);
      }
      return (
        <div className="flex">
          <Dropdown>
            <DropdownTrigger className="text-[16px] font-medium leading-[26px] tracking-normal font-mont">{value}</DropdownTrigger>
              <DropdownMenu className="w-full mt-1 bg-white border rounded-md shadow-lg">
                <DropdownOption onClick={() => handleClick("true")} className="text-green-600 capitalize px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer">
                  True
                </DropdownOption>
                <DropdownOption onClick={() => handleClick("false")} className="capitalize px-3 py-2 text-sm text-cherry opacity-100 hover:bg-gray-100 cursor-pointer">
                  False
                </DropdownOption>
              </DropdownMenu>
          </Dropdown>
        </div>
      );
    }
  };
  const moveToAnotherList  = {
    move_to_another_list: (value) => {
      const handleClick = (listName) => {
        moveVoter(rowDetail.id,state.activeListName,listName);
        setIsVoterModalOpenDetail(false);
        setIsVoterModalOpen(false);
      }
      const filteredOptions = (Array.isArray(state.listNames) ? state.listNames : []).filter((listName) => listName !== state.activeListName);
      return (
        <div className="flex">
          <Dropdown>
            <DropdownTrigger className="text-[16px] font-bold leading-[26px] tracking-normal font-mont">{value}</DropdownTrigger>
              <DropdownMenu className="w-full bg-white border rounded-md shadow-lg">
              {filteredOptions.map((listName, index) => (
                <DropdownOption
                  key={index}
                  onClick={() => handleClick(listName)}
                  className={`capitalize px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer li_${index}_${typeof listName === 'object' ? listName.name || listName.label || listName.toString() : listName}`}
                >
                  {typeof listName === 'object' ? listName.name || listName.label || listName.toString() : listName}
                </DropdownOption>
              ))}
              
              {filteredOptions.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500 italic">
                  No options available
                </div>
              )}
              </DropdownMenu>
          </Dropdown>
        </div>
      );
    }
  };
  const addVoter  = {
    add_voter: (value) => {
      const handleClick = (listName) => {
        addVoters(rowDetail);
        setIsVoterModalOpenDetail(false);
        setIsVoterModalOpen(false);
      }
      const filteredOptions = finalListNames;
      return (
        <div className="flex">
          <Dropdown>
            <DropdownTrigger className="text-[16px] font-medium leading-[26px] tracking-normal font-mont">{value}</DropdownTrigger>
              <DropdownMenu className="w-full mt-1 bg-white border rounded-md shadow-lg">
              {filteredOptions.map((listName, index) => (
                <DropdownOption
                  key={index}
                  onClick={() => handleClick(listName)}
                  className={`li_${index}_${typeof listName === 'object' ? listName.name || listName.label || listName.toString() : listName} capitalize px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer`}
                >
                  {typeof listName === 'object' ? listName.name || listName.label || listName.toString() : listName}
                </DropdownOption>
              ))}
              
              {filteredOptions.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500 italic">
                  No options available
                </div>
              )}
              </DropdownMenu>
          </Dropdown>
        </div>
      );
    }
  };
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check immediately on component mount
    checkScreenSize();
    
    // Also listen for resize events
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleModal = () => {
    setIsVoterModalOpenDetail(true);
  };
  const handleFields = (row) => {
    if(isListTableName === "mylistTable"){
      let array = [
        { key: 'firstName', value: row?.first_name, label: 'First Name' },
        { key: 'lastName', value: row?.last_name, label: 'Last Name' },
        { key: 'voted', value: row?.voted === '1' ? "Voted" : row?.voted === '0' ? "Not Voted" : "N/A", label: 'Harris County Voted' },
        // { key: 'sbisd_voted', value: row?.sbisd_voted === '1' ? "Voted" : row?.sbisd_voted === '0' ? "Not Voted" : "N/A", label: 'SBISD Voted' },
        { key: 'phoneNumber', value: row?.phone_number, label: 'Phone Number', type: 'phone', valueEmail: row?.email, isEdit: true },
        { key: 'id', value: row?.id , label: 'id' }
      ]
      return array
    }else if(isListTableName === "lookupTable"){
      return [
        { key: 'firstName', value: row?.first_name, label: 'First Name' },
        { key: 'middleName', value: row?.middle_name, label: 'Middle Name' },
        { key: 'lastName', value: row?.last_name, label: 'Last Name' },
        { key: 'streetName', value: row?.street_name, label: 'Street Name' },
        { key: 'zipCode', value: row?.zipcode, label: 'Zip Code' },
        { key: 'action', value: 'Add Voter', label: 'Actions',type: 'action' },
        { key: 'id', value: row?.id , label: 'id' }
      ]
    }else if(isListTableName === "adminTable"){
      return [
        { key: 'firstName', value: row?.full_name, label: 'First Name' },
        { key: 'phoneNumber', value: row?.phone_number, label: 'Phone Number', type: 'phone', valueEmail: row?.email },
        { key: 'email', value: row?.email, label: 'Email' },
        { key: 'networkSize', value: row?.total_members, label: 'Network Size' },
        { key: 'signupTimestamp', value: row?.signup_timestamp_central, label: 'Signup Timestamp' },
        { key: 'id', value: row?.id , label: 'id' }
      ]
    }else{
      return []
    }
  }
  const handleViewDetail = (row) => {
    if(isListTableName === "mylistTable"){
      setRowDetail(row);
      setIsVoterModalOpen(true);
      setVoterData({...row,voted:row?.voted === '1' ? "Voted" : row?.voted === '0' ? "Not Voted" : "N/A",sbisd_voted:row?.sbisd_voted === '1' ? "Voted" : row?.sbisd_voted === '0' ? "Not Voted" : "N/A"});
      let array = [
        { key: 'first_name', label: 'First Name' },
        { key: 'middle_name', label: 'Middle Name' },
        { key: 'last_name', label: 'Last Name' },
        { key: 'voter_id', label: 'Voter ID' },
        { key: 'voted', label: 'Harris County Voted' },
        { key: 'sbisd_voted', label: 'SBISD Voted' },
        { key: 'school_district', label: 'School District' },
        { key: 'general_voting_history', label: 'General Voting History' },
        { key: 'primary_voting_history', label: 'Primary Voting History' },
        { key: 'phone_number', label: 'Phone Number',isEdit: true },
        { key: 'user_email', label: 'Email' },
      ]
      setVoterFields(array);
    } else if(isListTableName === "lookupTable"){
      setRowDetail(row);
      setIsVoterModalOpen(true);
      setVoterData(row);
      setVoterFields([
        { key: 'first_name', label: 'First Name' },
        { key: 'middle_name', label: 'Middle Name' },
        { key: 'last_name', label: 'Last Name' },
        { key: 'voter_id', label: 'Voter ID' },
        { key: 'house_number', label: 'House Number' },
        { key: 'street_name', label: 'Street Name' },
        { key: 'zipcode', label: 'Zip Code' },
        { key: 'age', label: 'Age' },
        { key: 'city', label: 'City' },
        { key: 'precinct', label: 'Precinct' },
      ]);
    }else if(isListTableName === "adminTable"){
      setRowDetail(row);
      setIsVoterModalOpen(true);
      setVoterData(row);
      setVoterFields([
        { key: 'full_name', label: 'Full Name' },
        { key: 'voter_id', label: 'Voter ID' },
        { key: 'status', label: 'Status' },
        { key: 'phone_number', label: 'Phone Number' },
        { key: 'email', label: 'Email' },
        { key: 'total_members', label: 'Network Size' },
        { key: 'pct_ch_no', label: 'PCT NO' },
        { key: 'school_district_name', label: 'School District' },
        { key: 'role', label: 'Role' },
        { key: 'primary_voting_history', label: 'Primary Voting History' },
        { key: 'signup_timestamp_central', label: 'Signup Timestamp' },
        { key: 'multi_column', label: 'Multi Column' },
      ]);
    }
  };
  function handleSbisdFilter(e) {
    setSbisdFilter(e.target.checked);
    if (e.target.checked) {      
      setSearchParams({page:1});
      dispatch(updateFilters(['school_district', 21]));
      dispatch(fetchFilteredList({ navigate, params: { page: 1 } }));
    } else {
      setSearchParams({page:1});
      dispatch(resetFilters());
    }

  }
  const handleClickList = (val,row) => {
    addVoters(row);
    setIsVoterModalOpenDetail(false);
    setIsVoterModalOpen(false);
  }

  return (
    <TableProvider.Provider value={state}>
      {tableName === 'myList' && (
        <>
          {/* <div className="font-medium w-full text-center">
            <p className="sm:text-lg md:text-22px mb-5 font-bold text-[#9A0029] bg-[#FFE5E5] px-[30px] py-[10px] font-fira">
            If you have multiple Voter Lists, be sure to select the one you want to view by clicking the "My Voter List" dropdown. 
            </p>
          </div> */}
          <div className="self-start mb-5 text-center flex justify-center w-full">
            <ul className="list-disc list-inside space-y-2 text-base font-semibold text-left md:text-lg">
              <li>
                If you have multiple Voter Lists, be sure to select the one you want to view by clicking the "My Voter List" dropdown
              </li>
              <li>
                EACH column can be SORTED
              </li>
              <li>
                Select the Columns you want to show by using the Show/Hide Columns dropdown
              </li>
            </ul>
          </div>
          {/* <div className="mb-5 font-medium w-full text-center">Be sure to select the correct voter list by clicking on the "My Voter List" dropdown.</div> */}
        </>
      )}
      <div id="table-wrapper" className="flex flex-col w-full gap-2 relative">
        <div className={`flex lg:mb-[20px] gap-3 md:mb-[15px] mb-[2px] flex-col sm:flex-row ${(totalItems || tableName === 'LookupTable') ? 'justify-between' : 'justify-end'}`}>
            {tableName === 'LookupTable' && 
              <div>
                <div className="mx-auto">
                  <div className="">
                    <div className="flex items-center gap-2">
                      {/* <div className="inline-flex items-center gap-2 border border-red-500 rounded-lg p-2 bg-[#FFF5F7]">
                          <label className="flex items-center cursor-pointer relative mb-[3.2px]">
                              <input type="checkbox" 
                                  id="check2" 
                                  name="agreed"
                                  className="peer h-5 w-5 cursor-pointer transition-all appearance-none rounded shadow hover:shadow-md border border-slate-300 checked:bg-[#D30039] checked:border-[#D30039]"
                                  checked={sbisdFilter}
                                  onChange={handleSbisdFilter}
                              />
                              <span className="absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" stroke="currentColor" stroke-width="1">
                                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                                  </svg>
                              </span>
                          </label>
                          <span className="xl:text-[20px] font-normal xl:leading-[26px] text-[18px] leading-normal tracking-normal font-fira cursor-pointer select-none translate-y-[-1px]">
                            Search For SBISD Voters ONLY
                          </span>
                        </div> */}
                        <div className="flex">
                          <div className="mt-2">
                            {state.data.length > 0 && (<div className="text-18px"><span className="font-medium">Total Voters:</span> <span className="font-bold">{totalItems}</span></div>)}
                          </div>
                          <div className="ml-3">
                            {
                              state.data.length > 0 && (
                                <Dropdown
                                  className={tableName === 'adminTable' ? 'w-[60px]' : 'w-full'}
                                >
                                  <DropdownTrigger
                                    id="action"
                                    disabled={state.status.addVoters === "loading" || state.selectedRows.length <= 0}
                                    className={`h-6 text-sm text-[15px] disabled:opacity-70 font-bold w-full`}
                                  >
                                    <div className="h-11 p-3 font-extrabold rounded-md bg-cherry text-white hover:bg-white hover:text-cherry transition-all duration-200 border border-cherry relative disabled:cursor-not-allowed disabled:bg-cherry disabled:text-white disabled:opacity-70">
                                      ADD VOTERS
                                    </div>

                                  </DropdownTrigger>

                                  <DropdownMenu state={state.status.addVoter}>
                                    {listNames?.map(
                                      (opt1, i) =>
                                        (!opt1.condition) && (
                                          <DropdownOption
                                            key={i}
                                            onClick={() => {
                                              handleAddVotersToList(opt1);
                                            }}
                                            className={`li_${i}_${opt1.label} ${(opt1.label === 'MOVE to Another List' || opt1.label === 'DELETE VOTER') ? 'font-bold' : ''}`}
                                          >
                                            {opt1}
                                          </DropdownOption>
                                        )
                                    )}
                                  </DropdownMenu>
                                </Dropdown>
                              )
                            }
                          </div>
                        </div>
                    </div>
                  </div>
                </div>
              </div>
            }
            {(tableName === 'myList' && totalItems > 0) && (
            <div className="text-center mb-2 sm:text-left sm:mb-0 flex items-center">
                <span className="text-18px">
                  <span className="font-medium">Total Voters:</span> <span className="font-bold">{totalItems}</span>
                </span>
            </div>
            )}
          <div className="flex justify-end gap-2 flex-wrap-reverse">
            {tableName === 'myList' ? <>
              <div>
                {ThirdComponent && <ThirdComponent key={3} />}
                {FourthComponent && <FourthComponent key={4} />}
              </div>
              <div>
                {tableActions[2].label && (
                  <TableButton
                    key={2}
                    btnDisable={tableActions[2].btnDisable !== undefined ? tableActions[2].btnDisable : false}
                    {...tableActions[2]}
                  >
                    {tableActions[2].label}
                  </TableButton>
                )}
              </div>
              <div>
                <TableButton
                  key={1}
                  btnDisable={tableActions[1].btnDisable !== undefined ? tableActions[1].btnDisable : false}
                  {...tableActions[1]}
                >
                  {tableActions[1].label}
                </TableButton>
              </div>
              <div>
                {isMobile && isEdit === true ? null : <ToggleColumns />}
              </div>
              <div>
                {onStartTutorial ? isMobile && isEdit === true ? null : <PageHelper tableName={tableName} /> : null}
              </div>
              <div>
                {isMobile && isEdit === true ? null : tableName !== 'LookupTable' ? <ResetFilters setResetKey={setResetKey} /> : <></>}
              </div>
              <div>
                <TableButton
                  key={0}
                  btnDisable={tableActions[0].btnDisable !== undefined ? tableActions[0].btnDisable : false}
                  {...tableActions[0]}
                >
                  {tableActions[0].label}
                </TableButton>
              </div>
            </> :
              <>
                {tableActions.map(
                  (action, i) =>
                    (action.condition === undefined || action.condition) &&
                    (action.component ? (
                      <action.component key={i} />
                    ) : (
                      <TableButton
                        key={i}
                        btnDisable={action.btnDisable !== undefined ? action.btnDisable : false}
                        {...action}
                        style={{ order: action.order || "0" }}
                      >
                        {action.label}
                      </TableButton>
                    ))
                )}

                {onStartTutorial ? isMobile && isEdit === true ? null : <PageHelper tableName={tableName} /> : null}
                {isMobile && isEdit === true ? null : tableName !== 'LookupTable' ? <ResetFilters setResetKey={setResetKey} /> : <></>}
                {isMobile && isEdit === true ? null : <ToggleColumns />}
              </>
            }
          </div>
        </div>
        {
          isMobile && isEdit === true ? 
          <>
            {isListTableName === "lookupTable" ? <Filters isOnlyBtn={true} key={resetKey} columns={null} tableName={tableName} setResetKey={setResetKey} /> : null}
            {tableName === 'adminTable' ? <TableHead key={resetKey} isListTableName={isListTableName} tableName={tableName} isMobile={true}/> : null}
            {state.status.fetchData !== "loading" && (
              <div className="border-t border-t-[#E5E7EB] mt-[12px] pt-[20px]">
                {state.data.map((row, i) => (
                  <ResponsiveTableView 
                    key={i}
                    idx={i}
                    fields={handleFields(row)}
                    onViewDetails={() => handleViewDetail(row)}
                    rowDetail={row}
                    onCheckboxChange={(rows) => onToggleSelectRow(rows)}
                    filteredOptions={finalListNames}
                    handleClick={handleClickList}
                    isChecked={isListTableName === "mylistTable" ? true : false}
                  />
                ))}
                <div className="">
                  {state.data.length === 0 &&
                    state.status.fetchData !== "loading" &&
                    emptyPlaceholder &&
                    emptyPlaceholder(() => {
                      onResetFilters();
                      setResetKey((prev) => (prev += 1));
                    })}
                </div>
              </div>
            )}
          </>
          :
            <div className="w-full overflow-x-auto overflow-y-clip pb-[80px] relative">
              {onReorder && <DragPreview />}

              <table className="w-full [&_td]:px-2 [&_th]:px-2 border-t border-l border-r">
                <TableHead key={resetKey} isListTableName={isListTableName} tableName={tableName} />

                <tbody className="isolate relative z-10">
                  <Filters key={resetKey} tableName={tableName} setResetKey={setResetKey} />

                  {state.status.fetchData !== "loading" &&
                    state.data.map((row, i) => (
                      <TableRow key={i} row={row} index={i} actions={rowActions} tableName={tableName} />
                    ))}
                </tbody>
              </table>
              <div className={isListTableName !== "lookupTable" ? `md-lg:absolute md-lg:bottom-0 md-lg:w-full` : ""}>
                {state.data.length === 0 &&
                  state.status.fetchData !== "loading" &&
                  emptyPlaceholder &&
                  emptyPlaceholder(() => {
                    onResetFilters();
                    setResetKey((prev) => (prev += 1));
                  })}
              </div>
            </div>
        }

        {state.status.fetchData === "loading" && (
          <div className="absolute top-1/2 right-1/2 z-50">
            <Loading />
          </div>
        )}
        {state?.module === 'voterIdLookup' ? state.filteredList.length > 0 && <Pagination isEdit={isMobile && isEdit === true} totalItems={totalItems} /> : <Pagination isEdit={isMobile && isEdit === true} totalItems={totalItems} />}
      </div>
      {isVoterModalOpen && isMobile && isEdit === true && (
        <VoterDetailsModal 
          isOpen={isVoterModalOpen}
          onClose={() => setIsVoterModalOpen(false)}
          data={voterData}
          isShowDot={isListTableName === "mylistTable" || isListTableName === "adminTable" ? true : false}
          title="Voter Details"
          fields={voterFields}
          headerField="firstName"
          Overlay={true}
          modalClass="w-full"
          classWrapper={'items-end'}
          specialFields={
            isListTableName === "mylistTable" ? 
            {
              ...voterSpecialFields,
              ...primaryVotingHistory,
              ...generalVotingHistory
            } : isListTableName === "adminTable" ?  {...primaryVotingHistory,...voterSpecialFields} : {}
          }
          onOpenModal={handleModal}
        />
      )}
      {isVoterModalOpenDetail && isMobile && isEdit === true && (
        <VoterDetailsModal 
          isOpen={isVoterModalOpenDetail}
          Overlay={true}
          onClose={() => setIsVoterModalOpenDetail(false)}
          data={voterDataDetail}
          classWrapper={'items-center'}
          title=""
          fields={voterFieldsDetail}
          headerField="firstName"
          modalClass="w-[90%]"
          specialFields={
            isListTableName === "mylistTable" ? 
            {
              ...removeVoter,
              ...moveToAnotherList,
            } : isListTableName === "lookupTable" ? {
              ...addVoter
            } : isListTableName === "adminTable" ? {
              ...deleteData,
              ...deleteAccount,
              ...uploadData,
              ...editStatus,
              ...editMultiColumn
            } : {}
          }
        />
      )}
    </TableProvider.Provider>
  );
};

export default Table;
